import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { Copy, CheckCircle2, Clock, XCircle, RefreshCw } from "lucide-react";
import PublicShell from "../../components/PublicShell";
import { Button } from "../../components/ui/button";

function fmtBRL(v) {
  if (v === undefined || v === null) return "—";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(v));
}
function fmtExpires(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("pt-BR");
}
function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!target) return null;
  const t = new Date(target).getTime();
  const diff = Math.max(0, t - now);
  if (diff === 0) return "Expirado";
  const m = Math.floor(diff / 60000);
  const s = Math.floor((diff % 60000) / 1000).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function GuestPix() {
  const { token } = useParams();
  const [reservation, setReservation] = useState(null);
  const [pix, setPix] = useState(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/public/reservations/by-token/${token}`);
      // Backend retorna {reservation, house}
      setReservation({ ...r.data.reservation, house: r.data.house });
      const st = await api.get(`/public/payments/pix/status/${token}`);
      if (st.data.has_pix) setPix(st.data);
    } catch (e) {
      // ignore — pode ser 404 se token inválido
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Poll status cada 6s enquanto pix pending
  useEffect(() => {
    if (!pix) return;
    if (["approved", "rejected", "cancelled", "refunded", "expired"].includes(pix.status)) return;
    const id = setInterval(async () => {
      try {
        const st = await api.get(`/public/payments/pix/status/${token}`);
        if (st.data.has_pix) setPix(st.data);
      } catch {}
    }, 6000);
    return () => clearInterval(id);
  }, [pix, token]);

  async function gerar() {
    if (!reservation) return;
    setCreating(true);
    try {
      const r = await api.post("/public/payments/pix/create", { reservation_id: reservation.id });
      setPix({ ...r.data, has_pix: true, internal_status: r.data.status === "approved" ? "approved" : "payment_created" });
      toast.success("Pix gerado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível gerar o Pix.");
    } finally { setCreating(false); }
  }

  function copyCode() {
    if (!pix?.qr_code) return;
    navigator.clipboard.writeText(pix.qr_code);
    toast.success("Código Pix copiado");
  }

  const countdown = useCountdown(pix?.date_of_expiration);

  if (loading) return <PublicShell><div className="p-12 text-slate-500">Carregando…</div></PublicShell>;
  if (!reservation) return <PublicShell><div className="p-12">Reserva não encontrada.</div></PublicShell>;

  const status = pix?.status;
  const isApproved = status === "approved";
  const isExpired = status === "cancelled" || status === "expired" || countdown === "Expirado";

  return (
    <PublicShell>
      <div className="max-w-2xl mx-auto py-10 px-6" data-testid="guest-pix-page">
        <div className="mb-6">
          <span className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Pagamento da sua reserva</span>
          <h1 className="text-3xl font-semibold text-[#0AA89C] mt-1">Pagar com Pix</h1>
          <p className="text-sm text-slate-500 mt-1">Cartão de crédito estará disponível em breve.</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-6 mb-6">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Info label="Imóvel" value={reservation.house?.name} />
            <Info label="Reserva" value={reservation.code} />
            <Info label="Check-in" value={reservation.check_in} />
            <Info label="Check-out" value={reservation.check_out} />
            <Info label="Hóspedes" value={reservation.guests} />
            <Info label="Valor total" value={fmtBRL(reservation.total)} />
          </div>
        </div>

        {!pix && (
          <div className="bg-white border border-slate-200 rounded-md p-8 text-center">
            <div className="text-lg text-[#22302E] mb-2">Pague escaneando o QR Code ou usando o Pix copia e cola.</div>
            <p className="text-sm text-slate-500 mb-6">Ao clicar, geramos uma cobrança Pix individual segura via Mercado Pago.</p>
            <Button onClick={gerar} disabled={creating} className="bg-[#0AA89C] hover:bg-[#066B63] text-white px-8" data-testid="btn-gerar-pix">
              {creating ? "Gerando…" : "Gerar Pix"}
            </Button>
          </div>
        )}

        {/* Card institucional de confiança */}
        <div className="mt-6 bg-[#F7F4EF]/70 border border-[#0AA89C]/20 rounded-md p-5" data-testid="rs-card">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5 text-[#0AA89C]" />
            <h3 className="font-medium text-[#22302E]">Sua reserva está protegida</h3>
          </div>
          <p className="text-sm text-[#22302E]/70 mb-3">
            A Azuhomes acompanha sua reserva do início ao fim, oferecendo uma experiência organizada, documentada e transparente.
          </p>
          <ul className="text-xs text-[#22302E]/70 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 list-disc pl-4 mb-3">
            <li>Pagamento processado pelo Mercado Pago</li>
            <li>Contrato digital vinculado à reserva</li>
            <li>Check-in eletrônico</li>
            <li>Check-out eletrônico</li>
            <li>Histórico da jornada</li>
            <li>Atendimento Azuhomes</li>
          </ul>
          <a href="/reserva-segura" target="_blank" rel="noreferrer" className="text-sm text-[#0AA89C] hover:text-[#066B63]" data-testid="rs-card-link">
            Saiba como funciona a Reserva Segura →
          </a>
        </div>

        {pix && (
          <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="pix-card">
            <div className="flex items-center gap-2 mb-4">
              {isApproved ? (
                <span className="inline-flex items-center gap-1 text-emerald-700 text-sm font-medium"><CheckCircle2 className="w-4 h-4" /> Pagamento identificado</span>
              ) : isExpired ? (
                <span className="inline-flex items-center gap-1 text-red-600 text-sm font-medium"><XCircle className="w-4 h-4" /> Pix expirado</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-amber-700 text-sm font-medium"><Clock className="w-4 h-4" /> Aguardando pagamento</span>
              )}
              <span className="text-xs text-slate-500 ml-auto">
                {pix.date_of_expiration && !isApproved && countdown && countdown !== "Expirado" && (
                  <>Expira em <b>{countdown}</b></>
                )}
              </span>
            </div>

            {isApproved ? (
              <div className="text-center py-10">
                <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                <p className="text-lg text-[#22302E]">Recebemos a confirmação do seu pagamento.</p>
                <p className="text-sm text-slate-500 mt-2">A equipe Azuhomes fará a validação final e liberará sua reserva em seguida. Você receberá o contrato por e-mail.</p>
              </div>
            ) : isExpired ? (
              <div className="text-center py-6">
                <p className="text-slate-600 mb-4">O prazo deste Pix terminou. Gere um novo código para continuar.</p>
                <Button onClick={gerar} disabled={creating} className="bg-[#0AA89C] hover:bg-[#066B63] text-white">
                  <RefreshCw className="w-4 h-4 mr-1" /> Gerar novo Pix
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {pix.qr_code_base64 && (
                  <div className="flex flex-col items-center">
                    <img src={`data:image/png;base64,${pix.qr_code_base64}`} alt="QR Code Pix" className="w-56 h-56 border border-slate-200 rounded" data-testid="pix-qr-img" />
                    <p className="text-xs text-slate-500 mt-2 text-center">Escaneie com o app do seu banco</p>
                  </div>
                )}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-1">Pix copia e cola</div>
                  <textarea readOnly value={pix.qr_code || ""} rows={5} className="w-full text-xs border border-slate-200 rounded p-2 font-mono break-all" data-testid="pix-copy-paste" />
                  <Button onClick={copyCode} variant="outline" size="sm" className="mt-2 w-full" data-testid="btn-copy-pix">
                    <Copy className="w-3 h-3 mr-1" /> Copiar código
                  </Button>
                  <div className="mt-4 text-xs text-slate-500">
                    <div>Valor: <b>{fmtBRL(pix.amount)}</b></div>
                    {pix.date_of_expiration && <div>Validade: {fmtExpires(pix.date_of_expiration)}</div>}
                    {pix.payment_id && <div>Referência: <code>{pix.payment_id}</code></div>}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </PublicShell>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500">{label}</div>
      <div className="text-slate-700">{value || "—"}</div>
    </div>
  );
}
